// Pure helpers for the transcript screens: the transcript's speaker blocks, where a quoted span sits in them,
// per-speaker colour slots, and the wording of a takeaway's standing. Display only — "overdue" and "days late"
// come from the API (ADR 0003 §4); this file only says them in words.

import type { TakeawayDue } from '../types/transcripts'

export interface TranscriptBlock {
  index: number
  /** HH:MM:SS as written in the transcript, null for text before the first timecode */
  timecode: string | null
  speaker: string | null
  /** the block's lines joined with single spaces */
  text: string
}

const HEADER = /^\s*\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.+?):\s*$/

/** Splits "[HH:MM:SS] Speaker:" blocks; lines under a header are one paragraph. */
export function parseBlocks(text: string): TranscriptBlock[] {
  const blocks: TranscriptBlock[] = []
  let current: TranscriptBlock | null = null
  const lines: string[] = []
  const flush = () => {
    if (!current) return
    current.text = lines.join(' ').replace(/\s+/g, ' ').trim()
    if (current.text.length > 0 || current.timecode) blocks.push(current)
    lines.length = 0
  }
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const m = HEADER.exec(raw)
    if (m) {
      flush()
      current = { index: blocks.length, timecode: normalizeTimecode(m[1]), speaker: m[2].trim(), text: '' }
      continue
    }
    if (raw.trim().length === 0) continue
    if (!current) current = { index: 0, timecode: null, speaker: null, text: '' }
    lines.push(raw.trim())
  }
  flush()
  return blocks.map((b, i) => ({ ...b, index: i }))
}

/** "12:34" becomes "00:12:34"; brackets and spaces are dropped. */
export function normalizeTimecode(raw: string | null | undefined): string | null {
  if (!raw) return null
  const m = /(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(raw)
  if (!m) return null
  const pad = (s: string) => s.padStart(2, '0')
  return m[3] ? `${pad(m[1])}:${m[2]}:${m[3]}` : `00:${pad(m[1])}:${m[2]}`
}

/** A DOM-safe id for a timecode anchor. */
export function timecodeId(timecode: string): string {
  return 'tc-' + timecode.replace(/:/g, '-')
}

/**
 * Where a verbatim quote sits in a block's text, as [start, end) in the original string. Case and whitespace are
 * ignored; a quote the model trimmed or padded is found by its opening words. Null when it is not there.
 */
export function findQuote(haystack: string, quote: string): [number, number] | null {
  const q = fold(quote.replace(/^[\s"'“”‘’…]+|[\s"'“”‘’…]+$/g, ''))
  if (q.text.length < 6) return null
  const h = fold(haystack)
  const tries = [q.text, q.text.slice(0, 80), q.text.slice(0, 48), q.text.slice(0, 28)].filter((t, i, a) => t.length >= 12 && a.indexOf(t) === i)
  for (const t of tries) {
    const at = h.text.indexOf(t)
    if (at >= 0) return [h.map[at], h.map[at + t.length - 1] + 1]
  }
  return null
}

/** Lower-cased, whitespace-collapsed text with a map from folded index back to the original index. */
function fold(s: string): { text: string; map: number[] } {
  let text = ''
  const map: number[] = []
  let lastSpace = true
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (/\s/.test(c)) {
      if (lastSpace) continue
      text += ' '; map.push(i); lastSpace = true
      continue
    }
    const ch = c.toLowerCase().replace(/[“”]/g, '"').replace(/[‘’]/g, "'")
    text += ch; map.push(i); lastSpace = false
  }
  if (text.endsWith(' ')) { text = text.slice(0, -1); map.pop() }
  return { text, map }
}

/** The colour slot (1–8) for a speaker: their position in the room, cycled. */
export function speakerSlot(speakers: string[], name: string | null | undefined): number {
  if (!name) return 7
  const i = speakers.findIndex((s) => s.toLowerCase() === name.toLowerCase())
  return ((i >= 0 ? i : speakers.length) % 8) + 1
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`

/** The standing of a takeaway, in words: "promised Sep 2 · 8 days late". */
export function dueLabel(due: TakeawayDue, dueOn: string | null, formatDate: (iso: string) => string): string {
  const promised = dueOn ? `promised ${formatDate(dueOn)}` : ''
  switch (due.state) {
    case 'overdue': return `${promised} · ${plural(due.daysLate ?? 0, 'day')} late`
    case 'today': return `${promised} · due today`
    case 'ahead': return `${promised} · in ${plural(due.daysLeft ?? 0, 'day')}`
    case 'closed-late': return `${promised} · closed ${plural(due.daysLate ?? 0, 'day')} late`
    case 'closed-on-time': return `${promised} · closed on time`
    case 'closed': return 'closed'
    case 'dropped': return 'dropped'
    default: return 'no date promised'
  }
}
